import React from 'react';
import { gql } from '@apollo/client';
import {
  Datagrid,
  FunctionField,
  NullableBooleanInput,
  NumberField,
  SelectInput,
  TextField,
} from 'react-admin';
import List from 'components/List';
import IdField from 'components/IdField';
import DateTimeField from 'components/DateTimeField';
import { IdFilter } from 'components/Filter';
import DateFilter from 'components/Filter/DateFilter';
import CompanyInput from 'components/CompanyInput';
import LocationInput from 'components/LocationInput';
import ProfessionalInput from 'components/ProfessionalInput';
import { CASE_QUERY_OPTIONS, PermittedLinkField } from 'components/IntegrationCaseFields';
import ProvisioningReportField, { PROVISIONING_STATUS_CHOICES } from './ProvisioningReportField';

const EngagementCaseFilters = [
  <IdFilter />,
  <CompanyInput source="search.company_ids" alwaysOn />,
  <LocationInput alwaysOn />,
  <ProfessionalInput alwaysOn />,
  <SelectInput
    label="Provisioning Status"
    source="search.provisioning_statuses"
    choices={PROVISIONING_STATUS_CHOICES}
    emptyText="All"
    alwaysOn
  />,
  <NullableBooleanInput label="Has Report" source="search.has_report" alwaysOn />,
  <DateFilter label="Starts From" source="search.starts_date_gte" />,
  <DateFilter label="Starts To" source="search.starts_date_lte" />,
  <DateFilter label="Ends From" source="search.ends_date_gte" />,
  <DateFilter label="Ends To" source="search.ends_date_lte" />,
];

const EngagementCasesList = () => (
  <List
    filters={EngagementCaseFilters}
    exporter={false}
    queryOptions={CASE_QUERY_OPTIONS}
    data-testid="engagement-cases-list"
  >
    <Datagrid bulkActionButtons={false} rowClick="show">
      <IdField />
      <TextField label="Case UID" source="uid" sortable={false} />
      <PermittedLinkField
        label="Company"
        source="location.company.name"
        typename="Company"
        sortable={false}
      />
      <PermittedLinkField
        label="Location"
        source="location.name"
        typename="Location"
        sortable={false}
      />
      <PermittedLinkField
        label="Professional ID"
        source="professional_id"
        typename="Professional"
        sortable={false}
      />
      <FunctionField
        label="Name"
        render={(record) => [record.first_name, record.last_name].filter(Boolean).join(' ')}
      />
      <DateTimeField label="Starts Date" source="starts_date" format="MM/dd/yyyy" />
      <DateTimeField label="Ends Date" source="ends_date" format="MM/dd/yyyy" />
      <ProvisioningReportField label="Provisioning Report" sortable={false} />
      <NumberField label="Revision" source="state_revision" sortable={false} />
    </Datagrid>
  </List>
);

EngagementCasesList.query = gql`
  query GET_LIST($input: EngagementCaseQueryInput) {
    _engagementCasesMeta(input: $input) {
      count
    }
    engagementCases(input: $input) {
      id
      uid
      professional_id
      first_name
      last_name
      starts_date
      ends_date
      provisioning_status
      provisioning_freshness
      state_revision
      location {
        id
        name
        company {
          id
          name
        }
      }
    }
  }
`;

export default EngagementCasesList;
